/**
 * Client-side mirror of backend/src/game/dungeon-instance/config/item-defs.ts,
 * trimmed to the fields the vendor UI needs to browse and price the catalog
 * before a purchase (`rollWeight` is loot-roll-only, never shown to a
 * player). Same "no shared package, kept in sync by hand" convention as
 * config/character-roster.ts. The server remains authoritative — every
 * price shown here is a display estimate via sim/pricing.ts, the real price
 * is whatever POST /api/economy/vendor/buy actually returns.
 *
 * ## Why the words live here and not on the server (2026-09-04)
 *
 * User: "For each item, I should be able to read its usage and
 * characteristics. It should also be clear who the item is suitable for and
 * who it is not suitable for."
 *
 * Satchel charges, cosmetics and arms all carry their copy on the backend def
 * and it rides the wire, because all three are served. Curios are the one
 * catalogue with no endpoint — this mirror *is* the client's item vocabulary,
 * and it is read in-run by `ui/loot-panel.ts` and `ui/run-end-overlay.ts`
 * where a round trip is not wanted. Putting the prose on a server table that
 * nothing serves would have made it dead code, so it sits with the rest of
 * what the client already knows about an item.
 *
 * `name` is written out for the same reason. Until this pass every curio was
 * labelled by prettifying its id, which cannot produce an apostrophe: the
 * game showed "Miners Pick" and "Wardens Seal".
 *
 * ## What a curio actually is, and why every entry says the same thing
 *
 * A curio has exactly one use — carry it out and sell it — so `usage` is
 * nearly the same sentence twenty times, and the copy that matters is the
 * other two fields. Every one of them is really about the same decision:
 * this weighs something, your bag is finite, is it worth the space? The
 * detail sheet prints the Wax-per-kilo alongside, so the words name the trade
 * and the number settles it.
 */

import type { ItemCopy } from './item-copy';
import { N_, t } from '../i18n/i18n';
import { GEAR_LABELS } from './gear-defs';

export type LootRarity = 'COMMON' | 'RARE';

export interface ItemDef {
  id: string;
  /** Written out, not derived — a derived label cannot hold an apostrophe. */
  name: string;
  rarity: LootRarity;
  weightKg: number;
  baseValue: number;
  /**
   * The shallowest floor it drops on, in `Run.stratum`'s depth convention.
   * The vendor sells every entry regardless — this is loot-table gating, and
   * a shelf that hid the deep items would stop the shop from being a place to
   * learn what is down there.
   */
  minDepth: number;
  /**
   * The three lines the detail sheet reads. Same shape the served catalogues
   * carry on their own defs, so `ui/item-detail.ts` handles all four the same
   * way. See `config/item-copy.ts`.
   */
  copy: ItemCopy;
}

export const ITEM_DEFS: Record<string, ItemDef> = {
  // Found on every floor.
  rusty_sword: {
    id: 'rusty_sword', name: N_('Rusty Sword'), rarity: 'COMMON', weightKg: 1.2, baseValue: 15, minDepth: 1,
    copy: {
      usage: N_('Scrap steel off some delver who did not come back. You cannot swing it — it goes in the bag, comes out at the Sconce, and the vendor buys it by the pound.'),
      goodFor: N_('An early bag with room in it. It is one of the most common things in the Cellars, so it is usually the first thing you find and the reason a side room paid at all.'),
      poorFor: N_('A bag that is nearly full. It is near the bottom of the table for Wax per kilo — a candlestick weighs a third less and sells for more.'),
    },
  },
  silver_candlestick: {
    id: 'silver_candlestick', name: N_('Silver Candlestick'), rarity: 'COMMON', weightKg: 0.8, baseValue: 20, minDepth: 1,
    copy: {
      usage: N_('Household silver, tarnished but solid. Carried out and sold at the Sconce like every other curio.'),
      goodFor: N_('Everyone. It pays better per kilo than any common but the candles, which makes it the one piece of ordinary loot worth stopping for even on a deep run.'),
      poorFor: N_('Nobody, really — if you have any room left, take it. The only thing it loses to is an actual rare, and only when your bag is already full.'),
    },
  },
  tarnished_chalice: {
    id: 'tarnished_chalice', name: N_('Tarnished Chalice'), rarity: 'COMMON', weightKg: 1.0, baseValue: 18, minDepth: 1,
    copy: {
      usage: N_('Chapel plate that nobody came back for. Pure vendor fodder — carry it up, sell it, spend it.'),
      goodFor: N_('Cellars runs and half-empty bags. A middling rate for a middling weight; nothing about it is a mistake.'),
      poorFor: N_('The last kilo of a Vaults run. A full kilo for single-figure Wax is the sort of thing that stops you carrying a Warden’s Seal.'),
    },
  },
  bone_totem: {
    id: 'bone_totem', name: N_('Bone Totem'), rarity: 'COMMON', weightKg: 0.6, baseValue: 12, minDepth: 1,
    copy: {
      usage: N_('Someone’s charm, carved from something that used to walk. It does nothing for you. It sells.'),
      goodFor: N_('Filling gaps. It is light enough that it almost never costs you a better item, which is the whole case for a cheap common.'),
      poorFor: N_('Anyone counting on it. It is close to the least valuable thing you can pick up, so a run built out of totems is a run that barely paid.'),
    },
  },
  tallow_candles: {
    id: 'tallow_candles', name: N_('Tallow Candles'), rarity: 'COMMON', weightKg: 0.5, baseValue: 14, minDepth: 1,
    copy: {
      usage: N_('A bundle of rendered-fat candles, the thing this whole place is named for. Sold at the Sconce like everything else you drag up.'),
      goodFor: N_('Everyone, and especially a heavy delver: it is the best Wax-per-kilo of any common in the game. If you take one thing off a shelf, take these.'),
      poorFor: N_('Nothing much. The only complaint is the sticker price — you need a lot of bundles to feel it, so do not walk past a rare to reach one.'),
    },
  },
  mildewed_ledger: {
    id: 'mildewed_ledger', name: N_('Mildewed Ledger'), rarity: 'COMMON', weightKg: 0.7, baseValue: 16, minDepth: 1,
    copy: {
      usage: N_('Somebody’s accounts, gone soft with damp. Unreadable, and worth something to whoever buys such things at the Sconce.'),
      goodFor: N_('Most bags. Light, better than average per kilo, and common enough on the shallow floors to be a reliable filler.'),
      poorFor: N_('A bag down to its last few hundred grams — the candles and the candlestick both pay better for the space.'),
    },
  },
  iron_keyring: {
    id: 'iron_keyring', name: N_('Iron Keyring'), rarity: 'COMMON', weightKg: 0.5, baseValue: 11, minDepth: 1,
    copy: {
      usage: N_('A hoop of keys to doors that are no longer standing. Opens nothing in the dungeon; sells fine at the Sconce.'),
      goodFor: N_('Squeezing into a nearly-full bag. Half a kilo is little enough to fit almost anywhere, so it goes in where a proper find would not.'),
      poorFor: N_('Anyone hoping it unlocks something. It does not — no door in the dungeon takes a key, and its sticker value is near the bottom of the table.'),
    },
  },
  sour_wine: {
    id: 'sour_wine', name: N_('Sour Wine'), rarity: 'COMMON', weightKg: 0.6, baseValue: 10, minDepth: 1,
    copy: {
      usage: N_('A bottle that turned decades ago. You cannot drink it — it is not a consumable, it is cargo — and it sells for very little.'),
      goodFor: N_('A bag with room to spare and nothing better in the room. It is light, which is the only argument in its favour.'),
      poorFor: N_('Almost any real decision. It is among the cheapest things in the game and the rate is poor with it; if something else is on the shelf, take the other thing.'),
    },
  },
  miners_pick: {
    id: 'miners_pick', name: N_('Miner’s Pick'), rarity: 'COMMON', weightKg: 2.4, baseValue: 24, minDepth: 1,
    copy: {
      usage: N_('A working pick left where its owner dropped it. It breaks nothing down here — it is weight in your bag and Wax at the vendor.'),
      goodFor: N_('A run that has found nothing else. It has one of the higher sticker prices among commons, so it is a real pickup on an empty bag.'),
      poorFor: N_('Anyone with a bag half full. Nearly two and a half kilos at one of the worst rates in the game — five bundles of candles weigh the same and pay nearly three times as much.'),
    },
  },
  ale_keg: {
    id: 'ale_keg', name: N_('Ale Keg'), rarity: 'COMMON', weightKg: 4.8, baseValue: 30, minDepth: 1,
    copy: {
      usage: N_('A full keg, still sealed. The single heaviest common in the game, and the vendor pays for it like the ale it is rather than the effort it took.'),
      goodFor: N_('A first Cellars run where you have found nothing at all and have the whole bag free. It is the highest sticker price of any common.'),
      poorFor: N_('Everyone else, honestly. It is the worst Wax-per-kilo in the entire game — nearly five kilos, and the same space carries a Vault Coffer worth thirty times as much.'),
    },
  },
  gilded_reliquary: {
    id: 'gilded_reliquary', name: N_('Gilded Reliquary'), rarity: 'RARE', weightKg: 3.5, baseValue: 300, minDepth: 1,
    copy: {
      usage: N_('A gilt casket for a saint’s finger bone, minus the finger. A rare — worth more on its own than a bag full of commons, and worth insuring before a run.'),
      goodFor: N_('Any delver who finds one, on any floor. Even at three and a half kilos it pays for the space many times over.'),
      poorFor: N_('The last slot of a Vaults bag, where a Warden’s Seal weighs a seventh as much and pays three times more. A good problem to have.'),
    },
  },
  wardens_key: {
    id: 'wardens_key', name: N_('Warden’s Key'), rarity: 'RARE', weightKg: 0.4, baseValue: 260, minDepth: 1,
    copy: {
      usage: N_('The key a warden carried, cast in something that is not iron. Opens nothing — it is worth what it is made of. Carry it out and sell it, or insure it and try again.'),
      goodFor: N_('Everyone, always, immediately. Four hundred grams for a rare’s price is the best rate of anything on the shallow floors; there is no bag too full for this.'),
      poorFor: N_('Nobody. If you are ever choosing against it, something has gone wrong with the choosing.'),
    },
  },
  coin_hoard: {
    id: 'coin_hoard', name: N_('Coin Hoard'), rarity: 'RARE', weightKg: 2.2, baseValue: 300, minDepth: 1,
    copy: {
      usage: N_('Somebody’s life savings in a heap. Not spendable as it stands — the vendor weighs it and pays you in Wax like everything else.'),
      goodFor: N_('Any run with two spare kilos. It ties the Reliquary among the shallow floors’ chest rares and weighs a good deal less.'),
      poorFor: N_('A bag already carrying a Coffer out of the Vaults. Two kilos is two kilos, and down there the competition is much richer.'),
    },
  },
  silver_service: {
    id: 'silver_service', name: N_('Silver Service'), rarity: 'RARE', weightKg: 1.8, baseValue: 240, minDepth: 1,
    copy: {
      usage: N_('A boxed set of table silver, complete and unspoiled. Straight to the vendor, like every curio.'),
      goodFor: N_('Almost every bag. It is the lightest of the shallow-floor rares bar the Warden’s Key, which makes it the easy yes when your capacity is getting tight.'),
      poorFor: N_('Nothing on the Cellars or the Ossuary. On a Vaults run it is the first rare you would put down to make room for a Seal or a Coffer.'),
    },
  },
  apothecary_case: {
    id: 'apothecary_case', name: N_('Apothecary’s Case'), rarity: 'RARE', weightKg: 0.9, baseValue: 270, minDepth: 1,
    copy: {
      usage: N_('A physician’s travelling case, bottles intact. None of it is drinkable and none of it heals you — satchel charges are bought at the Shop. This is cargo, and it is rich cargo.'),
      goodFor: N_('Everyone. Under a kilo for nearly the top shallow-floor price makes it the second-best rate on the shallow floors, and it fits into almost any remaining space.'),
      poorFor: N_('Anyone expecting medicine out of it. It is worth Wax, not health; if you want healing, the Salve is on the Satchel shelf.'),
    },
  },

  // The Ossuary and below.
  gilded_pick: {
    id: 'gilded_pick', name: N_('Gilded Pick'), rarity: 'RARE', weightKg: 2.6, baseValue: 620, minDepth: 2,
    copy: {
      usage: N_('A ceremonial pick, gold over the head, never swung at anything. Found no shallower than the Ossuary. Carried out and sold.'),
      goodFor: N_('Ossuary runs and deeper. It is worth a good deal more than the shallow floors’ chest rares at a comparable weight, which is most of the reason a contract for the second floor pays for itself.'),
      poorFor: N_('Cellars delvers, who will never see one — it does not drop up there. And a full Vaults bag, where two and a half kilos has better uses.'),
    },
  },
  warden_regalia: {
    id: 'warden_regalia', name: N_('Warden’s Regalia'), rarity: 'RARE', weightKg: 3.8, baseValue: 700, minDepth: 2,
    copy: {
      usage: N_('The full dress of a warden of the deep floors: chain, chasing, and a great deal of thread. Ossuary and below. It is not wearable — the paperdoll is arms only. It sells.'),
      goodFor: N_('Ossuary and Vaults runs with room. It has one of the highest sticker prices outside the Vaults, so it is often the single item that makes a contract profitable.'),
      poorFor: N_('Anyone near their carry limit — nearly four kilos is the heaviest rare short of the Coffer. And anyone hoping to wear it; there is no armour slot in this game to put it in.'),
    },
  },
  gold_ore_cluster: {
    id: 'gold_ore_cluster', name: N_('Gold Ore Cluster'), rarity: 'RARE', weightKg: 4.2, baseValue: 560, minDepth: 2,
    copy: {
      usage: N_('Raw gold still in the rock, hacked out and left behind by whoever was hacking. Ossuary and below; sold by weight at the Sconce.'),
      goodFor: N_('An Ossuary run with a nearly empty bag. On its own it is a good haul, and it is among the commonest of the second-floor rares.'),
      poorFor: N_('Almost anyone carrying anything else. Four kilos for the worst rate of any rare in the game — on the Vaults it is the first thing you should leave.'),
    },
  },

  // The Vaults only.
  vault_coffer: {
    id: 'vault_coffer', name: N_('Vault Coffer'), rarity: 'RARE', weightKg: 5.5, baseValue: 1100, minDepth: 3,
    copy: {
      usage: N_('A strongbox out of the vaults themselves, still locked and heavy with it. The most valuable single object in the game. Vaults only — nothing shallower drops one.'),
      goodFor: N_('A Vaults delver who came in light. It is worth more than an entire good run on the floors above, and it is why a Vaults contract is worth its price.'),
      poorFor: N_('A heavy loadout. Five and a half kilos is over a fifth of your capacity — with a greatsword and a full satchel you may simply not be able to lift it and much else besides.'),
    },
  },
  wardens_seal: {
    id: 'wardens_seal', name: N_('Warden’s Seal'), rarity: 'RARE', weightKg: 0.5, baseValue: 950, minDepth: 3,
    copy: {
      usage: N_('The seal of office of whoever held the vaults, half a kilo of dense metal. Vaults only. Sells for nearly what the Coffer does at a tenth of the weight.'),
      goodFor: N_('Everyone who reaches the Vaults. The best Wax-per-kilo in the game by a distance — finding one is the single best thing that can happen to a delve.'),
      poorFor: N_('Nobody at all. If your bag is too full for half a kilo, put down the Ale Keg.'),
    },
  },

  // ─── The rest of the shelf (2026-09-07) ───────────────────────────────
  // Twelve more things a chest can hold. The chest-density pass roughly
  // tripled how many boxes a run puts in front of you, and twenty entries
  // spread over twenty chests meant seeing the same bottle three times a
  // floor. Nothing here is a new *kind* of decision — it is all still weight
  // against value — but a shelf you have not memorised is a shelf worth
  // reading.
  larder_platter: {
    id: 'larder_platter', name: N_('Larder Platter'), rarity: 'COMMON', weightKg: 0.7, baseValue: 12, minDepth: 1,
    copy: {
      usage: N_('A serving platter left laid, with something on it nobody should eat. You cannot eat it either — food is not a mechanic here — so it goes in the bag as cargo.'),
      goodFor: N_('An early bag with plenty of room. There is nothing wrong with it and nothing remarkable either, which is exactly what a floor of commons is made of.'),
      poorFor: N_('The last kilo of a deep run. A boxed silver service is the same idea done properly and pays twenty times better for less than three times the weight.'),
    },
  },
  bottled_ink: {
    id: 'bottled_ink', name: N_('Bottled Ink'), rarity: 'COMMON', weightKg: 0.5, baseValue: 13, minDepth: 1,
    copy: {
      usage: N_('A stoppered bottle of oak-gall ink, still black. Somebody at the Sconce keeps ledgers and pays for it. That is the whole of its use.'),
      goodFor: N_('Light bags and clerks. It is one of the better small commons per kilo, and it slots into space nothing else will fit.'),
      poorFor: N_('Anyone hoping it is a potion. It is not drinkable and it is not a satchel charge — the bottles that do something are bought at the Shop.'),
    },
  },
  cellar_bucket: {
    id: 'cellar_bucket', name: N_('Cellar Bucket'), rarity: 'COMMON', weightKg: 0.9, baseValue: 11, minDepth: 1,
    copy: {
      usage: N_('An iron-banded bucket, dry for a century. It carries nothing for you — your bag is your bag — and it sells for the metal on it.'),
      goodFor: N_('A first run with an empty bag and a long way still to walk. It is a pickup, not a plan.'),
      poorFor: N_('Almost any bag with something already in it. Nearly a kilo for one of the poorest rates on the shelf; the candles beside it pay more than twice as well.'),
    },
  },
  salt_crate: {
    id: 'salt_crate', name: N_('Salt Crate'), rarity: 'COMMON', weightKg: 1.5, baseValue: 18, minDepth: 1,
    copy: {
      usage: N_('A small crate of preserving salt, caked solid. Worth real money above ground, and heavy for what it is.'),
      goodFor: N_('A bag with room and a floor that has not paid yet. It has one of the better sticker prices among the commons.'),
      poorFor: N_('A delver already near their limit. A kilo and a half is a kilo and a half, and there is nearly always something lighter in the same room.'),
    },
  },
  grey_grimoire: {
    id: 'grey_grimoire', name: N_('Grey Grimoire'), rarity: 'COMMON', weightKg: 0.8, baseValue: 17, minDepth: 1,
    copy: {
      usage: N_('A grey-bound book of somebody’s working notes. You cannot read it and it teaches you nothing — no talent, no ability, no spell. It sells well for a book.'),
      goodFor: N_('Most bags on most floors. One of the better sticker prices among the light commons, and under a kilo with it.'),
      poorFor: N_('Anyone expecting it to do something. Talents come from levelling and abilities come from your class; a book in your bag is cargo.'),
    },
  },
  iron_lockbox: {
    id: 'iron_lockbox', name: N_('Iron Lockbox'), rarity: 'COMMON', weightKg: 1.2, baseValue: 20, minDepth: 1,
    copy: {
      usage: N_('A small banded strongbox, empty and still locked. Nothing down here opens it and there is nothing inside if it did — what you are carrying out is the box.'),
      goodFor: N_('Any bag with a spare kilo. It matches the candlestick for sticker price and it turns up far more often than a rare does.'),
      poorFor: N_('Anyone who thinks it is a container. Your carry limit is your carry limit; a box in your bag does not hold anything else in your bag.'),
    },
  },
  coopers_stool: {
    id: 'coopers_stool', name: N_('Cooper’s Stool'), rarity: 'COMMON', weightKg: 1.8, baseValue: 19, minDepth: 1,
    copy: {
      usage: N_('A round working stool from a cellar bench, seat worn smooth. Somebody at the Sconce will take the timber off your hands, and that is all it does.'),
      goodFor: N_('A run that has found nothing and has the whole bag free. It is worth a good deal for a piece of furniture.'),
      poorFor: N_('Almost anyone with cargo already. Getting on for two kilos at one of the poorest rates in the game is the sort of thing this game asks you to walk past.'),
    },
  },
  hall_bench: {
    id: 'hall_bench', name: N_('Hall Bench'), rarity: 'COMMON', weightKg: 2.6, baseValue: 26, minDepth: 1,
    copy: {
      usage: N_('A short bench out of a dining hall, one end scorched. There is nothing to do with it but drag it up the stairs and sell the timber and the ironwork.'),
      goodFor: N_('A first Cellars run with nothing else in hand. Its sticker price is near the top of the commons and it is a good deal lighter than a full keg.'),
      poorFor: N_('Anything but an empty bag. Over two and a half kilos at a poor rate — this is the second-worst thing in the game to lug up a staircase, and the worst is the keg.'),
    },
  },
  heralds_shield: {
    id: 'heralds_shield', name: N_('Herald’s Shield'), rarity: 'RARE', weightKg: 1.6, baseValue: 250, minDepth: 1,
    copy: {
      usage: N_('A painted heater shield off a hall wall, arms still bright on it. It blocks nothing — it is cargo, not an off-hand, and off-hands are bought at the Shop. It sells like a rare.'),
      goodFor: N_('Every bag that finds one. Under two kilos for a rare’s price is one of the better rates on the shallow floors, and it is among the commonest of the shallow rares.'),
      poorFor: N_('The last space in a Vaults bag, where the competition is worth four times as much for less weight. A pleasant problem.'),
    },
  },
  veined_ore: {
    id: 'veined_ore', name: N_('Veined Ore'), rarity: 'RARE', weightKg: 3.6, baseValue: 540, minDepth: 2,
    copy: {
      usage: N_('A slab of rock shot through with something bright. Ossuary and below. Sold by weight, like every other lump of the deep floors.'),
      goodFor: N_('An Ossuary run with a mostly empty bag. On its own it turns a mediocre delve into a good one.'),
      poorFor: N_('Anyone near their carry limit. It is a heavy rare and its rate reflects that — on the Vaults it is among the first things to put back down.'),
    },
  },
  gilded_trunk: {
    id: 'gilded_trunk', name: N_('Gilded Trunk'), rarity: 'RARE', weightKg: 4.0, baseValue: 620, minDepth: 2,
    copy: {
      usage: N_('A small banded trunk with gilt corners, still shut. Nothing opens it down here and nothing needs to — the vendor buys it as it stands. Ossuary and below.'),
      goodFor: N_('A deep run that has gone well and still has room. It is one of the highest sticker prices outside the Vaults themselves.'),
      poorFor: N_('A heavy loadout with a full satchel. Four kilos is a sixth of everything you can carry, and it will cost you two lighter rares to take it.'),
    },
  },
  wardens_codex: {
    id: 'wardens_codex', name: N_('Warden’s Codex'), rarity: 'RARE', weightKg: 2.4, baseValue: 1000, minDepth: 3,
    copy: {
      usage: N_('The warden’s own book, lying open where it was left, leaf edges gilt. There is nothing to learn from it — no talent, no ability — and there is a very great deal to sell. Vaults only.'),
      goodFor: N_('Everyone who gets that deep. It is the second most valuable object in the game and it weighs less than half what the Coffer does.'),
      poorFor: N_('Nothing much, on its own terms. If you are choosing against it, you are choosing a Warden’s Seal, and that is the only fair comparison it loses.'),
    },
  },

  // ─── Off the dead (2026-09-07) ────────────────────────────────────────
  // User: "we can also add a system where players can loot the mobs they have
  // killed, and those mobs can also drop some kind of loot."
  //
  // A separate pool from the chests, and it should read like one: this is the
  // kit a skeleton was holding, not goods somebody stored. Nearly all of it
  // is cheap, and that is the design — a body is worth two seconds, not a
  // decision. What it actually buys is pace. The walk between rooms used to
  // be pure cost.
  //
  // None of it is equippable, and every entry says so, because a game that
  // hands you an axe off a corpse and then will not let you swing it owes you
  // that sentence up front. Arms are bought at the Shop.
  notched_dagger: {
    id: 'notched_dagger', name: N_('Notched Dagger'), rarity: 'COMMON', weightKg: 0.4, baseValue: 9, minDepth: 1,
    copy: {
      usage: N_('A short blade off a skeleton’s belt, edge gone to teeth. You cannot fight with it — arms come from the Shop — so it goes in the bag and out again at the vendor.'),
      goodFor: N_('Any bag at all. It is among the lightest things the dead carry, so searching a body is rarely a waste of space even when it is nearly a waste of time.'),
      poorFor: N_('Anyone stopping over every corpse in a swarm. Six of these is barely a bundle of candles, and the time spent is the real cost.'),
    },
  },
  broken_arrows: {
    id: 'broken_arrows', name: N_('Broken Arrows'), rarity: 'COMMON', weightKg: 0.3, baseValue: 6, minDepth: 1,
    copy: {
      usage: N_('A fistful of snapped shafts with the heads still on. There is no bow in this game to use them with; the heads are worth their iron and nothing more.'),
      goodFor: N_('Squeezing into a bag that has nothing left in it. Almost weightless, which is the only argument for it and a real one.'),
      poorFor: N_('Any run where you are counting. It is worth less than anything else you can pick up, dead or otherwise.'),
    },
  },
  splintered_quiver: {
    id: 'splintered_quiver', name: N_('Splintered Quiver'), rarity: 'COMMON', weightKg: 0.7, baseValue: 12, minDepth: 1,
    copy: {
      usage: N_('A cracked leather quiver, straps perished. It holds nothing for you — your bag does not work that way — and the leather sells.'),
      goodFor: N_('A light bag on a busy floor. Among the better things the small dead leave behind.'),
      poorFor: N_('A bag with real cargo in it. Two-thirds of a kilo for a common’s price is space a chest could have used better.'),
    },
  },
  cracked_blade: {
    id: 'cracked_blade', name: N_('Cracked Blade'), rarity: 'COMMON', weightKg: 1.0, baseValue: 13, minDepth: 1,
    copy: {
      usage: N_('A skeleton’s sword, split down the fuller. It will not hold an edge and you cannot equip it — it is scrap steel that happens to be sword-shaped.'),
      goodFor: N_('An early Cellars bag. It is the most ordinary thing the dead carry and it turns a corridor patrol into a small payment.'),
      poorFor: N_('Anything past the shallow floors. A full kilo at a poor rate is exactly what a Vaults delver should be walking past.'),
    },
  },
  rotted_buckler: {
    id: 'rotted_buckler', name: N_('Rotted Buckler'), rarity: 'COMMON', weightKg: 1.1, baseValue: 14, minDepth: 1,
    copy: {
      usage: N_('A small shield with the boards gone soft, boss still bright. It blocks nothing — it is cargo, not an off-hand, and off-hands are bought at the Shop.'),
      goodFor: N_('A bag with room on a shallow floor. The boss is brass and the vendor pays for brass.'),
      poorFor: N_('Anyone who thinks it will help them. It cannot be equipped, and even if it could, an off-hand in this game is a look and a weight.'),
    },
  },
  barrow_stave: {
    id: 'barrow_stave', name: N_('Barrow Stave'), rarity: 'COMMON', weightKg: 1.3, baseValue: 15, minDepth: 1,
    copy: {
      usage: N_('A skeleton mage’s stave, the focus at its head gone dull and dead. There is no magic left in it and none of your abilities care that you are holding it. It sells for the wood and the setting.'),
      goodFor: N_('An empty bag early in a delve. It pays better than the smallest scraps and costs almost nothing to take.'),
      poorFor: N_('A druid hoping for a better focus, and anyone with a full bag. Neither gets what they want out of it.'),
    },
  },
  pitted_axe: {
    id: 'pitted_axe', name: N_('Pitted Axe'), rarity: 'COMMON', weightKg: 1.4, baseValue: 16, minDepth: 1,
    copy: {
      usage: N_('A hand axe eaten half through with rust, still gripped when you found it. Not equippable; the head is worth its iron at the counter.'),
      goodFor: N_('A run that has found nothing yet. It sells for more than most of what the dead leave behind.'),
      poorFor: N_('Deep bags. Nearly a kilo and a half at a rate the shallow commons already beat — this is the sort of pickup a Vaults run cannot afford.'),
    },
  },
  bone_mace: {
    id: 'bone_mace', name: N_('Bone Mace'), rarity: 'COMMON', weightKg: 1.6, baseValue: 17, minDepth: 1,
    copy: {
      usage: N_('A club of banded bone and iron, the sort a Rattler swings. In your hands it does nothing at all — it is not a weapon you can equip, only weight you can sell.'),
      goodFor: N_('A wide-open bag on the Cellars, where nothing better is competing for the space.'),
      poorFor: N_('Nearly everyone else. It is heavy for its price, and the rate is among the worst in the game.'),
    },
  },
  warped_crossbow: {
    id: 'warped_crossbow', name: N_('Warped Crossbow'), rarity: 'COMMON', weightKg: 1.9, baseValue: 22, minDepth: 1,
    copy: {
      usage: N_('A crossbow with the prod sprung and the string long gone. It cannot be drawn, it cannot be equipped, and there is no ranged combat in this game to want it for. The mechanism is worth good iron.'),
      goodFor: N_('An early bag with two spare kilos. It is one of the two best sticker prices the dead carry.'),
      poorFor: N_('Any careful delver. Almost two kilos is a Warden’s Key and a Seal put together, and it pays a fraction of either.'),
    },
  },
  grave_scythe: {
    id: 'grave_scythe', name: N_('Grave Scythe'), rarity: 'COMMON', weightKg: 2.0, baseValue: 21, minDepth: 1,
    copy: {
      usage: N_('The blade off a scythe-armed skeleton, snapped from its haft. It is not a polearm you can carry into a fight — reach weapons are bought — and the steel sells.'),
      goodFor: N_('A first Cellars run and an empty bag, where two kilos costs you nothing you would rather have.'),
      poorFor: N_('Every bag past the first few rooms. Its rate is at the bottom of the table, keg excepted.'),
    },
  },
  tomb_shield: {
    id: 'tomb_shield', name: N_('Tomb Shield'), rarity: 'COMMON', weightKg: 2.3, baseValue: 24, minDepth: 1,
    copy: {
      usage: N_('A full-sized grave shield, face rotted off its frame. It blocks nothing, it cannot be equipped, and the vendor buys the banding.'),
      goodFor: N_('A bag that is entirely empty. It has the highest sticker price of anything the ordinary dead leave behind.'),
      poorFor: N_('Almost every real situation. Over two kilos at a poor rate is the classic mistake this game asks you to stop making.'),
    },
  },
  bonewright_cleaver: {
    id: 'bonewright_cleaver', name: N_('Bonewright Cleaver'), rarity: 'RARE', weightKg: 3.2, baseValue: 520, minDepth: 1,
    copy: {
      usage: N_('The heavy cleaver a Bonewright fights with, taken off it once it stops. Cargo, not a weapon — you cannot equip it, and the Shop is where arms come from. It is worth a great deal of Wax.'),
      goodFor: N_('Anyone willing to take a Bonewright rather than walk around one. This is what beating one is worth, and it drops from the very first floor.'),
      poorFor: N_('A bag already carrying two rares out of the Vaults. Three kilos is real weight, and down there the same space holds something worth twice as much.'),
    },
  },
  tomb_maul: {
    id: 'tomb_maul', name: N_('Tomb Maul'), rarity: 'RARE', weightKg: 3.4, baseValue: 700, minDepth: 2,
    copy: {
      usage: N_('A great two-handed maul off something that was guarding a deep room. Ossuary and below. It is not equippable and never will be; it sells for what it is made of.'),
      goodFor: N_('An Ossuary delver who fights rather than avoids. It sells for as much as anything a chest on that floor holds, and it comes out of a fight you were going to have anyway.'),
      poorFor: N_('A heavy loadout near its limit. With a greatsword and a full satchel you may simply not have the kilos, and it is the sort of thing that costs you two lighter finds.'),
    },
  },
  warden_greataxe: {
    id: 'warden_greataxe', name: N_('Warden’s Greataxe'), rarity: 'RARE', weightKg: 4.6, baseValue: 900, minDepth: 2,
    copy: {
      usage: N_('The axe a Bone Warden carries, and the single heaviest thing the dead will ever hand you. Ossuary and below. It cannot be equipped — it is worth Wax, not damage.'),
      goodFor: N_('A party that killed the Warden and came in light. Nothing else off a body is worth close to this, and killing the thing that held it is the hardest fight on the floor.'),
      poorFor: N_('A bag with anything much in it already. It is nearly a fifth of everything you can carry, and on the Vaults a Seal weighs a tenth as much for most of the price.'),
    },
  },
};

/**
 * The number that decides whether a curio is worth its space.
 *
 * Quoted against the *sell* price rather than the sticker value, because sell
 * is what actually arrives in your wallet — `sim/pricing.ts` pays half of
 * `baseValue`. Kept here rather than in the detail sheet so the loot panel and
 * the stash can sort by it later without re-deriving anything.
 */
export function waxPerKg(def: Pick<ItemDef, 'baseValue' | 'weightKg'>, sellPriceWax: number): number {
  if (def.weightKg <= 0) return sellPriceWax;
  return Math.round((sellPriceWax / def.weightKg) * 10) / 10;
}

/**
 * Human-readable label for an itemDefId, in the active language.
 *
 * Reads the catalogue first and falls back to prettifying the id, so an item
 * a run sends down that this mirror has not been taught still names itself
 * rather than rendering blank.
 */
export function itemLabel(itemDefId: string): string {
  const def = ITEM_DEFS[itemDefId];
  if (def) return t(def.name);
  // Worn gear rides the carried-item pipeline too, under its own table.
  const gear = GEAR_LABELS[itemDefId];
  if (gear) return t(gear.name);
  return itemDefId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
